import React, { useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import axios from 'axios';
import { useSelector } from 'react-redux';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import Global from '../../../../store/global-selectors';
import { useConnectLabelProviderContext } from '../../../../components/modals/connect-label-provider-modal/context-provider';
import { countryToProviderIds } from './providerIds-by-countries';
import messages from '../messages';

import style from './carriers-additional.module.less';
import { CarrierFeeBanner } from './components/carrier-fee-banner';
import { AddCarriersGridTile } from './add-carriers-grid-tile';

const AddCarriersGrid = () => {
  const { onConnectLabelProvider, trackCarrierConnectionStep, WorkflowSteps, EventSourceStrings } = useConnectLabelProviderContext();
  const [displayProviders, setDisplayProviders] = useAsyncSafeState([]);
  const homeCountry = useSelector(Global.selectSellerHomeCountry);

  useEffect(() => {
    axios
      .get('/provider/availableProviders')
      .then((response) => {
        const providersAvailableToSeller = response.data.providers || [];
        const promotedProviderIdsForCountry = countryToProviderIds[homeCountry] || [];
        const providersToDisplay = promotedProviderIdsForCountry
          .map((id) => providersAvailableToSeller.find((p) => `${p.providerId}` === `${id}`))
          .filter((p) => !!p);

        setDisplayProviders(providersToDisplay);
      })
      .catch((e) => {
        NotificationService.showNotification({
          content: (
            <FormattedMessage
              defaultMessage='An unexpected error occurred trying to fetch available carrier connections. Please try reloading your browser.'
              description='error message when unable to find a list of available carriers'
              id='JQj+1I'
            />
          ),
          notificationType: NOTIFICATION_TYPE_ERROR,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
        // oxlint-disable-next-line no-console
        console.error('unable to fetch availableProviders', e);
      });
  }, [homeCountry, setDisplayProviders]);

  const openProviderSelector = useCallback(
    (evt) => {
      evt.preventDefault();
      onConnectLabelProvider();
      trackCarrierConnectionStep(WorkflowSteps.START, {
        source: EventSourceStrings.SETTINGS_CARRIERS_PAGE,
      });
    },
    [WorkflowSteps, onConnectLabelProvider, trackCarrierConnectionStep, EventSourceStrings],
  );

  const onTileClick = useCallback(
    (provider) => {
      onConnectLabelProvider(provider);
      trackCarrierConnectionStep(WorkflowSteps.START, {
        source: EventSourceStrings.SETTINGS_CARRIERS_PAGE,
      });
    },
    [EventSourceStrings.SETTINGS_CARRIERS_PAGE, WorkflowSteps.START, onConnectLabelProvider, trackCarrierConnectionStep],
  );

  return (
    <div className={style['additional-carriers']}>
      <div className={style.header}>
        <div className={style.title}>
          <FormattedMessage {...messages.byoaHeader} />
        </div>
        <div className={style.description}>
          <FormattedMessage {...messages.byoaDescriptionLine1} />
        </div>
        <div>
          <FormattedMessage {...messages.byoaDescriptionLine2} />
        </div>
      </div>
      <CarrierFeeBanner />
      <div className={style.tiles}>
        {displayProviders.map((provider, index) => (
          <AddCarriersGridTile key={`${provider.providerId}-${index}`} onConnectLabelProvider={onTileClick} provider={provider} />
        ))}
      </div>
      <div className={style.footer}>
        <Button onClick={openProviderSelector} theme={THEME_CONSTANTS.link}>
          <FormattedMessage {...messages.seeAllCarriers} />
        </Button>
      </div>
    </div>
  );
};

export default AddCarriersGrid;
